<template>
  <section ref="heroRef"
           class="person-hero"
           :style="dynamicStyle"
  >
    <h1
        class="person-hero__title"
        v-html="title"
    />

    <div
        class="person-hero__inner">
      <AppPersonMask :image="image"/>
    </div>

    <div
        class="person-hero__content">
      <AppRichText v-if="introduction" :data="introduction" class="person-hero__rich-text app-rich-text--introduction"/>
      <AppTagButtonList v-if="belongsTo" :data="belongsTo" class="person-hero__tag-button-list"/>
    </div>

  </section>
</template>

<script setup lang="ts">

import {computed, onMounted, reactive, ref, watch} from 'vue'
import {useWindowScroll} from '~/composables/useWindowScroll'
import {TTag} from "~/types/other-types";

const heroRef = ref<HTMLElement>()

const props = defineProps<{
  title: String,
  introduction: String,
  image: unknown,
  belongsTo: TTag[] | undefined
}>()

const state = reactive({
  scroll: 0
})

const dynamicStyle = computed(() => {
  const height = heroRef.value?.getBoundingClientRect().height | 0
  const opacity = (state.scroll) ? (height - state.scroll) / height : 1
  return {opacity: (opacity < 0) ? 0 : (opacity > 1) ? 1 : opacity}
})

onMounted(() => {
  // watch for scroll
  const scrollState = useWindowScroll()
  watch(scrollState, (newState) => {
    state.scroll = newState.scroll
  })
})

</script>

<style scoped lang="postcss">
.person-hero {
  top: 0px;
  z-index: 0;
  row-gap: 1.5rem;
  opacity: 0;
  @mixin app-layout-container;
  @mixin app-block-margins;
  padding-top: calc(16px + var(--navbar-padding));

  @media (--md) {
    position: sticky;
    padding-top: 8rem;
  }

  @media (--lg) {
    row-gap: 2rem;
  }

  &__title {
    grid-column: 1 / -1;
    text-align: center;
    font-family: Midnight, sans-serif;
    font-size: 28px;
    text-transform: uppercase;
    line-height: 0.8;

    @media (--md) {
      font-size: 54px;
    }

    @media (--lg) {
      font-size: 66px;
    }

    @media (--xl) {
      grid-column: span 10 / span 10;
      grid-column-start: 2;
      font-size: 92px;
    }
  }

  &__inner {
    grid-column: 1 / -1;
    padding-left: 0.5rem;
    padding-right: 0.5rem;

    @media (--md) {
      grid-column: span 6 / span 6;
      padding-left: 0px;
      padding-right: 0px;
    }

    @media (--lg) {
      grid-column: span 5 / span 5;
      grid-column-start: 2;
    }

    @media (--xl) {
      grid-column: span 5 / span 5;
      grid-column-start: 2;
    }

    @media (--xxl) {
      grid-column: span 4 / span 4;
      grid-column-start: 3;
    }
  }

  &__content {
    grid-column: 1 / -1;

    @media (--md) {
      grid-column: span 6 / span 6;
      grid-column-start: 7;
    }

    @media (--lg) {
      grid-column: span 5 / span 5;
    }

    @media (--xxl) {
      grid-column: span 4 / span 4;
      grid-column-start: 7;
    }
  }

  &__rich-text {
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;

    @media (--md) {
      padding-left: 0px;
      padding-right: 0px;
      text-align: left;
    }
  }

  &__tag-button-list {
    display: flex;
    justify-content: center;
    padding-top: 1rem;

    @media (--md) {
      justify-content: flex-start;
    }
  }

  @media screen and (max-width: 767px) {
    opacity: 1 !important;
  }
}
</style>
